import MultiSelectSearchDropdown from '../../../../../common/ManageDealDrawer/Components/MultiSelectSearchDropdown/MultiSelectSearchDropdown';
import { getFinancialYear } from '../../../../../utils/dateUtils';
import { applyFyFloor, sameFySet } from './fyFloor';

const FY_CONTROL_WIDTH = '15rem';

interface FyMultiSelectProps {
    availableFys: number[];
    selectedFys: number[];
    onChange: (fys: number[]) => void;
}

/** Options are exactly `availableFys` — audited FYs only (PD12). The floor of ONE selected year
 *  lives here, in this component's own onChange wrapper, not in the shared dropdown; that dropdown
 *  was only extended with two opt-in props (`showClearAll`, `showSelectionAsText`) whose defaults
 *  leave its other render sites unchanged (A-121). "Financial Year" is the header's own left-hand
 *  label, so the floating label is off and `placeholder` shows only on the empty selection the
 *  floor prevents. */
function FyMultiSelect({ availableFys, selectedFys, onChange }: Readonly<FyMultiSelectProps>) {
    const options = availableFys.map((fy) => ({ value: String(fy), label: `FY’${String(getFinancialYear(fy))}` }));
    const selectedOptions = selectedFys.map(String);
    // Display order only (toggling/floor logic above is Set-like and order-independent) — the
    // closed text summary always names the latest selected year first, whatever order the
    // selection itself was built in.
    const selectedOptionsForDisplay = [...selectedOptions].sort((a, b) => Number(b) - Number(a));

    const handleChange = (next: (string | null)[]) => {
        const floored = applyFyFloor(
            next.filter((fy): fy is string => fy !== null),
            selectedOptions,
        );
        if (sameFySet(floored, selectedOptions)) return;
        onChange(floored.map(Number));
    };

    return (
        <MultiSelectSearchDropdown
            placeholder="Financial Year"
            options={options}
            selectedItems={selectedOptionsForDisplay}
            toggleSelection={handleChange}
            disabled={availableFys.length <= 1}
            showFloatingLabel={false}
            showClearAll={false}
            showSelectionAsText
            // Fixed width (the dropdown sheet is 100% of it) so clearing "Select All" cannot shrink the
            // control or truncate the option labels. Padding matches the Units control beside it: 0
            // top/bottom for the 36px box, room on the right for the chevron.
            wrapperStyle={{ width: FY_CONTROL_WIDTH, padding: '0 2rem 0 0.75rem' }}
        />
    );
}

export default FyMultiSelect;
